'use client';

import { FC } from 'react';

import { Price } from '@ravago/shared/page-data/models/elements';
import { ShoppingCartItemWithOptionalSlug } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

interface Props {
   shoppingCartItem: ShoppingCartItemWithOptionalSlug;
   locale: string;
}

export const CheckoutSummaryItemElement: FC<Props> = ({ shoppingCartItem, locale }) => {
   const translate = useTranslations();
   const format = useFormatter();

   const totalPricePerProduct: Price = {
      amount: shoppingCartItem.unitPrice.price.amount * shoppingCartItem.quantity.amount,
      currency: shoppingCartItem.unitPrice.price.currency
   };

   return (
      <Paper>
         <Box direction="column" gap="md">
            <Box justify="between">
               <Link
                  disabled={!shoppingCartItem.slug}
                  underline={!shoppingCartItem.slug ? 'none' : 'always'}
                  href={!shoppingCartItem.slug ? '' : `/${locale}/products/${shoppingCartItem.slug}`}
               >
                  <Typography variant="body-highlight" color={!shoppingCartItem.slug ? 'subtle' : 'inherit'}>
                     {shoppingCartItem.product.description}
                  </Typography>
               </Link>

               <Typography variant="body-highlight" color="primary">
                  {format.price(totalPricePerProduct)}
               </Typography>
            </Box>
            <BreakpointSwitch includeViews={['phone', 'desktop']}>
               <Box gap="sm" direction="column">
                  <Box items="center" gap="sm">
                     <BoxItem basis="100%">
                        <Typography variant="caption">{translate('shopping-cart.product-reference')}</Typography>
                     </BoxItem>
                     <BoxItem basis="100%">
                        <Typography variant="small">{shoppingCartItem.customerProductReference}</Typography>
                     </BoxItem>
                  </Box>
                  <Box items="center" gap="sm">
                     <BoxItem basis="100%">
                        <Typography variant="caption">{translate('shopping-cart.quantity')}</Typography>
                     </BoxItem>
                     <BoxItem basis="100%">
                        <Typography variant="small">
                           {shoppingCartItem.quantity.amount} {shoppingCartItem.quantity.unitOfMeasure}
                        </Typography>
                     </BoxItem>
                  </Box>

                  <Box items="center" gap="sm">
                     <BoxItem basis="100%">
                        <Typography variant="caption">{translate('shopping-cart.packaging')}</Typography>
                     </BoxItem>
                     <BoxItem basis="100%">
                        <Typography variant="small">{shoppingCartItem.packaging}</Typography>
                     </BoxItem>
                  </Box>
                  <Box items="center" gap="sm">
                     <BoxItem basis="100%">
                        <Typography variant="caption">{translate('shopping-cart.unit-price')}</Typography>
                     </BoxItem>
                     <BoxItem basis="100%">
                        <Typography variant="small">{format.unitPrice(shoppingCartItem.unitPrice)}</Typography>
                     </BoxItem>
                  </Box>
               </Box>
            </BreakpointSwitch>
            <BreakpointSwitch excludeViews={['phone', 'desktop']}>
               <Box direction="row">
                  <BoxItem basis="250px">
                     <Box direction="column" gap="xs">
                        <Box items="center" gap="sm">
                           <BoxItem basis="100%">
                              <Typography variant="caption">{translate('shopping-cart.product-reference')}</Typography>
                           </BoxItem>
                           <BoxItem basis="100%">
                              <Typography variant="small">{shoppingCartItem.customerProductReference}</Typography>
                           </BoxItem>
                        </Box>
                        <Box items="center" gap="sm">
                           <BoxItem basis="100%">
                              <Typography variant="caption">{translate('shopping-cart.quantity')}</Typography>
                           </BoxItem>
                           <BoxItem basis="100%">
                              <Typography variant="small">
                                 {shoppingCartItem.quantity.amount} {shoppingCartItem.quantity.unitOfMeasure}
                              </Typography>
                           </BoxItem>
                        </Box>
                     </Box>
                  </BoxItem>
                  <BoxItem basis="250px">
                     <Box direction="column" gap="xs">
                        <Box items="center" gap="sm">
                           <BoxItem basis="100%">
                              <Typography variant="caption">{translate('shopping-cart.packaging')}</Typography>
                           </BoxItem>
                           <BoxItem basis="100%">
                              <Typography variant="small">{shoppingCartItem.packaging}</Typography>
                           </BoxItem>
                        </Box>
                        <Box items="center" gap="sm">
                           <BoxItem basis="100%">
                              <Typography variant="caption">{translate('shopping-cart.unit-price')}</Typography>
                           </BoxItem>
                           <BoxItem basis="100%">
                              <Typography variant="small">
                                 {format.unitPrice(shoppingCartItem.unitPrice)}/{shoppingCartItem.unitPrice.unitOfMeasure}
                              </Typography>
                           </BoxItem>
                        </Box>
                     </Box>
                  </BoxItem>
               </Box>
            </BreakpointSwitch>
         </Box>
      </Paper>
   );
};
